'use client';

import { useEffect, useRef, useState } from 'react';
import { ProgressBar } from './ProgressBar';
import { ChecklistItem, type ChecklistItemWarning } from './ChecklistItem';
import { AppDownloadModal } from './AppDownloadModal';
import styles from './progresschecklist.module.scss';

export interface ChecklistItemData {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    defaultChecked?: boolean;
    href?: string;
    linkType?: 'app-download';
    mobileHref?: string;
    qrCodeSrc?: string;
    warning?: ChecklistItemWarning;
}

export interface ProgressCheckListProps {
    items: ChecklistItemData[];
    id?: string;
    username?: string;
}

interface ModalState {
    qrCodeSrc: string;
}

export function ProgressCheckList({ items, id, username }: ProgressCheckListProps) {
    const storageKey = useRef(id ? `pcl:${id}` : `pcl:${items.map((i) => i.title).join('|')}`);

    // Fix #1: removed separate defaultChecked ref; items is stable (RSC props),
    // so it's safe to access directly in the mount-only effect below
    const [checkedItems, setCheckedItems] = useState<boolean[]>(
        () => items.map((item) => item.defaultChecked ?? false),
    );
    const [modal, setModal] = useState<ModalState | null>(null);

    useEffect(() => {
        try {
            const stored = sessionStorage.getItem(storageKey.current);
            if (stored) {
                const parsed: unknown = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length === items.length) {
                    setCheckedItems(
                        (parsed as boolean[]).map((val, i) => (items[i].defaultChecked ? true : Boolean(val))),
                    );
                }
            }
        } catch {
            // sessionStorage unavailable
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkedCount = checkedItems.filter(Boolean).length;
    const progressValue = items.length > 0 ? Math.round((checkedCount / items.length) * 100) : 0;

    // Fix #2: compute next inline; write to storage outside the state updater
    function handleItemClick(index: number) {
        if (checkedItems[index]) return;
        const next = checkedItems.map((val, i) => (i === index ? true : val));
        setCheckedItems(next);
        try {
            sessionStorage.setItem(storageKey.current, JSON.stringify(next));
        } catch {
            // sessionStorage unavailable
        }
    }

    return (
        <>
            <div className={styles.container}>
                {username && (
                    <p className={styles.username}>Username: {username}</p>
                )}
                <ProgressBar value={progressValue} />
                {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                <ul className={styles.list} role="list">
                    {items.map((item, index) => (
                        <li key={index} className={styles.listItem}>
                            <ChecklistItem
                                icon={item.icon}
                                title={item.title}
                                subtitle={item.subtitle}
                                stepNumber={index + 1}
                                checked={checkedItems[index]}
                                onClick={() => handleItemClick(index)}
                                href={item.href}
                                linkType={item.linkType}
                                mobileHref={item.mobileHref}
                                onAppDownloadClick={
                                    // Fix #4: inline setter, no separate handler needed
                                    item.linkType === 'app-download' && item.qrCodeSrc
                                        ? () => setModal({ qrCodeSrc: item.qrCodeSrc! })
                                        : undefined
                                }
                                warning={item.warning}
                            />
                        </li>
                    ))}
                </ul>
            </div>
            <AppDownloadModal
                isOpen={!!modal}
                onClose={() => setModal(null)}
                qrCodeSrc={modal?.qrCodeSrc ?? ''}
            />

        </>
    );
}
